import { queryOptions, type QueryClient } from '@tanstack/react-query';
import type { Tag as UIAPITag } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { parseTag, tagKeys, type Tag } from './entity';

/**
 * Tag read queries.
 *
 * Tags arrive sideloaded from the campaign cluster, but we still expose
 * `getTagQueryOptions` with a real fetch-capable `queryFn` so that
 * follow-the-FK reads via `useCampaignTags` degrade safely if a tag's
 * detail slot has been evicted (GC, workspace clear, manual invalidate)
 * before its parent campaign — the suspense query refetches from the
 * by-id endpoint instead of returning `undefined as Tag`.
 *
 * The "campaigns for tag" inverse view (`useCampaignsForTag`) lives in
 * `campaign/queries.ts` to avoid a circular import — it's a campaign
 * read parameterized by a tag id, so the campaign module is its honest
 * home.
 */

const STALE_TIME = 60_000;

/**
 * The `qc` parameter is unused today — `getTagById` returns just the
 * single tag with no sideloads to propagate — but it stays in the
 * signature to mirror `getCampaign(wsId, id, qc)` and `getAction(wsId,
 * id, qc)`. When tags grow a sideload (e.g. usage counts), the
 * populator hook is here without callers having to thread `qc` in
 * after the fact.
 */
/** @public */
export async function getTag(workspaceId: number, tagId: string, _qc: QueryClient): Promise<Tag> {
  const raw = await hydraUiApi.tags.get({
    params: {
      path: { environment_id: String(workspaceId), tag_id: tagId },
    },
  });
  if (raw.tag == null) {
    throw new Error('missing tag in get tag response');
  }
  return parseTag(raw.tag);
}

export const getTagQueryOptions = (workspaceId: number, tagId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: tagKeys.detail(workspaceId, tagId),
    queryFn: () => getTag(workspaceId, tagId, qc),
    staleTime: STALE_TIME,
  });

/**
 * Every tag the workspace has, for a picker that must offer tags no row
 * carries yet.
 *
 * The wire shape rather than the parsed entity: a tag's numeric id is what
 * every tag-attaching endpoint takes, and the entity's coercion to a string
 * id exists for the campaign sideload path, where the surrounding entities
 * use string ids. The list's own consumers do not read the timestamps the
 * entity parses, so nothing is gained by paying for them.
 *
 * @public
 */
export async function getWorkspaceTags(workspaceId: number, signal?: AbortSignal): Promise<UIAPITag[]> {
  const raw = await hydraUiApi.tags.list({ params: { path: { environment_id: String(workspaceId) } }, signal });
  return raw.tags;
}

/** @public */
export const getWorkspaceTagsQueryOptions = (workspaceId: number) =>
  queryOptions({
    queryKey: tagKeys.lists(workspaceId),
    queryFn: ({ signal }) => getWorkspaceTags(workspaceId, signal),
    staleTime: STALE_TIME,
    // A failed read leaves a picker offering nothing, which reads as a
    // workspace with no tags rather than as a failure.
    meta: { errorMessage: "Your tags couldn't be loaded. Try again." },
  });
